import React, { useContext, useEffect, useRef } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import PropTypes from 'prop-types';
import EditProductView from '../products/EditProductView';
import EditProductVariantsView from '../products/EditProductVariantsView';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import { useVendorBackPath } from '../../../helpers/vendorHelper';
import { triggerNotification } from '../../../helpers/notification';

const VendorProductDetailsView = ({ newProduct }) => {
  const navigate = useNavigate();
  const fetchedUuid = useRef(null);
  const { pathname } = useLocation();
  const { ecomVendorProductUuid } = useParams();
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchVendorProduct,
    createVendorProducts,
    updateVendorProducts,
    deleteVendorProducts,
    fetchVendorSetupSummary,
    importExternalProducts,
    regenerateVendorProductDesignImages
  } = useContext(VendorContext);
  const { backPath } = useVendorBackPath(
    ecomVendorProductUuid && pathname.includes('/duplicate')
      ? `/vendor/products/${ecomVendorProductUuid}`
      : `/vendor/products/`
  );

  const ecomVendorProduct =
    state.ecomVendorProduct.value?.uuid === ecomVendorProductUuid
      ? state.ecomVendorProduct.value
      : null;

  const ecomVendor = authState.ecomVendors.find(
    (v) => v.pkEcomVendor.toString() === authState.pkEcomVendor?.toString()
  );
  const isLoading =
    (ecomVendorProductUuid && !fetchedUuid.current) ||
    state.ecomVendorProduct.loading;

  const ecomVendorCollections =
    state.ecomVendorCollections.value
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({
        value: c.pkEcomVendorCollection.toString(),
        label: c.name
      })) ?? [];

  const imageUploadBaseUrl = `ecom-vendor-${authState.pkEcomVendor}-product`;

  useEffect(() => {
    if (
      ecomVendorProductUuid &&
      ecomVendorProductUuid !== fetchedUuid.current &&
      !newProduct
    ) {
      // eslint-disable-next-line no-use-before-define
      fetchVendorProduct(ecomVendorProductUuid, null, (error) => {
        triggerNotification('Product not found!');
      });
      fetchedUuid.current = ecomVendorProductUuid;
    }
  }, [ecomVendorProductUuid]);

  const onCreateProduct = (requestData, onSuccessCallback, onErrorCallback) => {
    createVendorProducts(
      authState.pkEcomVendor,
      { ecomVendorProducts: [requestData], selectFirstItem: true },
      (data) => {
        navigate(`/vendor/products/${data[0]?.uuid}`);
        fetchedUuid.current = data[0]?.uuid;
        if (
          state.ecomVendorSetupSummary.value?.ecomVendor.sandboxEnabled &&
          !state.ecomVendorSetupSummary.value?.hasProducts
        ) {
          fetchVendorSetupSummary(authState.pkEcomVendor, {skipLoading: true});
        }
        onSuccessCallback(data);
      },
      onErrorCallback
    );
  };

  const onUpdateProduct = (requestData, onSuccessCallback, onErrorCallback) => {
    updateVendorProducts(
      authState.pkEcomVendor,
      { ecomVendorProducts: [requestData], selectFirstItem: true },
      onSuccessCallback,
      onErrorCallback
    );
  };

  const onDeleteProduct = (
    pkEcomVendorProduct,
    onSuccessCallback,
    onErrorCallback
  ) => {
    deleteVendorProducts(
      authState.pkEcomVendor,
      [pkEcomVendorProduct],
      (data) => {
        navigate('/vendor/products');
        if (state.ecomVendorSetupSummary.value?.ecomVendor.sandboxEnabled) {
          fetchVendorSetupSummary(authState.pkEcomVendor, {skipLoading: true});
        }
        onSuccessCallback(data);
      },
      onErrorCallback
    );
  };

  const onSyncExternalProduct = (
    requestData,
    onSuccessCallback,
    onErrorCallback
  ) => {
    importExternalProducts(
      authState.pkEcomVendor,
      requestData,
      (data) => {
        fetchVendorProduct(ecomVendorProductUuid);
        onSuccessCallback(data);
      },
      onErrorCallback
    );
  };

  const onRegenerateProductDesignImages = (
    requestState,
    onSuccessCallback,
    onErrorCallback
  ) => {
    regenerateVendorProductDesignImages(
      authState.pkEcomVendor,
      [
        {
          ...requestState,
          pkEcomVendorProduct: ecomVendorProduct.pkEcomVendorProduct
        }
      ],
      (data) => {
        fetchVendorProduct(ecomVendorProductUuid);
        onSuccessCallback(data);
      },
      onErrorCallback
    );
  };

  const productDetailsProps = {
    ecomVendorProduct,
    onCreateProduct,
    onUpdateProduct,
    onDeleteProduct,
    onSyncExternalProduct,
    productId: ecomVendorProductUuid,
    collectionsList: ecomVendorCollections,
    isLoading,
    baseUrl: `/vendor/products`,
    imageUploadBaseUrl,
    backPath,
    ecomVendor,
    productUuid: ecomVendorProductUuid,
    onRegenerateProductDesignImages
  };

  return (
    <Routes>
      <Route
        element={
          <EditProductVariantsView {...productDetailsProps} newVariant />
        }
        path="/variants/new"
      />
      <Route
        element={<EditProductVariantsView {...productDetailsProps} />}
        path="/variants/:variantUuid"
      />
      <Route
        element={<EditProductVariantsView {...productDetailsProps} duplicate />}
        path="/variants/:variantUuid/duplicate"
      />
      <Route
        element={<EditProductVariantsView {...productDetailsProps} />}
        path="/variants"
      />
      <Route
        element={<EditProductView {...productDetailsProps} duplicate />}
        path="/duplicate"
      />
      <Route element={<EditProductView {...productDetailsProps} />} path="/" />
      <Route element={<Navigate replace to="/vendor/products" />} path="*" />
    </Routes>
  );
};

VendorProductDetailsView.propTypes = { newProduct: PropTypes.bool };

export default VendorProductDetailsView;
